/**
 * QR enrolment landing page `/e` (ARCHITECTURE §9.3). The single-use token rides the URL fragment,
 * so it never reaches access logs. On mount we redeem it, scrub the fragment with replaceState, and
 * then behave exactly like a fresh login that landed in PENDING.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { enroll } from './api.js';

type State = { k: 'redeeming' } | { k: 'pending'; pairingCode?: string } | { k: 'error' };

export function Enroll(): ReactNode {
  const [state, setState] = useState<State>({ k: 'redeeming' });

  useEffect(() => {
    const token = location.hash.replace(/^#/, '').trim();
    // scrub the token from the URL immediately
    history.replaceState(null, '', location.pathname);
    if (!token) return setState({ k: 'error' });
    void (async () => {
      const r = await enroll(token);
      if (r.kind === 'ok') {
        if (r.pairingCode) sessionStorage.setItem('lw.pairingCode', r.pairingCode);
        setState({ k: 'pending', ...(r.pairingCode ? { pairingCode: r.pairingCode } : {}) });
      } else setState({ k: 'error' });
    })();
  }, []);

  if (state.k === 'redeeming') return <div className="auth-splash">Enrolling this device…</div>;
  if (state.k === 'error') {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <h1 className="auth-title">Enrolment failed</h1>
          <p className="auth-note">This enrolment link is invalid, already used, or expired. Ask your admin for a new QR code.</p>
        </div>
      </div>
    );
  }
  return (
    <div className="auth-screen">
      <div className="auth-card pending">
        <div className="pending-spinner" aria-hidden />
        <h1 className="auth-title">Device enrolled</h1>
        <p className="auth-note">Waiting for an admin to approve this device.</p>
        {state.pairingCode ? (
          <div className="pairing" data-testid="pairing-code">
            <span className="pairing-label">Read this code to the admin</span>
            <span className="pairing-code">{state.pairingCode.slice(0, 3)}-{state.pairingCode.slice(3)}</span>
          </div>
        ) : null}
        <button className="auth-link" onClick={() => (location.href = '/')}>Continue</button>
      </div>
    </div>
  );
}
